import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import Header from '../../components/Header/Header';
import { getStoredUser, clearStoredUser, deleteAppUser, subscribeAuthChanged } from '../../lib/appAuth';
import { listBookingsTrusted } from '../../lib/bookingsApi';
import './Profile.css';

const Profile = ({ onGoHome, onNavigate }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => getStoredUser());
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [bookingsError, setBookingsError] = useState('');

  useEffect(() => {
    const u = getStoredUser();
    if (!u) {
      navigate('/signin', { replace: true });
      return;
    }
    setUser(u);
  }, [navigate]);

  useEffect(() => {
    const refresh = () => {
      const u = getStoredUser();
      if (!u?.id || !u?.email) {
        setBookings([]);
        setBookingsLoading(false);
        return;
      }
      setBookingsLoading(true);
      setBookingsError('');
      listBookingsTrusted(u)
        .then(setBookings)
        .catch((err) => {
          setBookingsError(err.message || 'Could not load bookings');
        })
        .finally(() => setBookingsLoading(false));
    };

    refresh();
    return subscribeAuthChanged(refresh);
  }, []);

  const formatBookingDate = (value) => {
    if (!value) return '—';
    const d = typeof value === 'string' ? value.split('T')[0] : value;
    try {
      return new Date(d).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return String(value);
    }
  };

  const handleSignOut = () => {
    clearStoredUser();
    navigate('/');
    window.scrollTo(0, 0);
  };

  const openDeleteDialog = () => {
    setDeletePassword('');
    setDeleteError('');
    setDeleteOpen(true);
  };

  const closeDeleteDialog = () => {
    if (!deleteLoading) {
      setDeleteOpen(false);
      setDeletePassword('');
      setDeleteError('');
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletePassword.trim()) {
      setDeleteError('Enter your password to confirm.');
      return;
    }
    setDeleteError('');
    setDeleteLoading(true);
    try {
      await deleteAppUser(user.email, deletePassword);
      clearStoredUser();
      setDeleteOpen(false);
      navigate('/', { replace: true });
      window.scrollTo(0, 0);
    } catch (err) {
      setDeleteError(err.message || 'Could not delete account. Try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="profile-page">
      <Header onLogoClick={onGoHome} onNavigate={onNavigate} />
      <main className="profile-main">
        <div className="profile-container">
          <Typography variant="h4" component="h1" className="profile-title" gutterBottom>
            Your profile
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Account details from your registration.
          </Typography>
          <Paper elevation={2} className="profile-card">
            <Box className="profile-field">
              <Typography variant="caption" color="text.secondary">
                Name
              </Typography>
              <Typography variant="body1">
                {user.firstName} {user.lastName}
              </Typography>
            </Box>
            <Box className="profile-field">
              <Typography variant="caption" color="text.secondary">
                Email
              </Typography>
              <Typography variant="body1">{user.email}</Typography>
            </Box>
            {user.phone ? (
              <Box className="profile-field">
                <Typography variant="caption" color="text.secondary">
                  Phone
                </Typography>
                <Typography variant="body1">{user.phone}</Typography>
              </Box>
            ) : null}
            <Box className="profile-actions">
              <Button type="button" variant="outlined" color="primary" onClick={handleSignOut}>
                Sign out
              </Button>
              <Button type="button" variant="outlined" color="error" onClick={openDeleteDialog}>
                Delete account
              </Button>
            </Box>
          </Paper>

          <Typography variant="h5" component="h2" className="profile-title" sx={{ mt: 4, mb: 1 }}>
            Your bookings
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Reservations saved when you complete checkout while signed in.
          </Typography>

          {bookingsError ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {bookingsError}
            </Alert>
          ) : null}

          <Paper elevation={2} className="profile-card" sx={{ mb: 4 }}>
            {bookingsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : bookings.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No saved bookings yet. Sign in, complete a booking, and you will see it here.
              </Typography>
            ) : (
              <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {bookings.map((b) => (
                  <Box component="li" key={b.id} sx={{ py: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {b.hotelName || 'Hotel'}
                      {b.hotelPropertyId ? ` · ${b.hotelPropertyId}` : ''}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatBookingDate(b.checkIn)} → {formatBookingDate(b.checkOut)}
                      {b.nights != null ? ` · ${b.nights} night${b.nights === 1 ? '' : 's'}` : ''}
                    </Typography>
                    {b.roomSummary ? (
                      <Typography variant="body2" color="text.secondary">
                        {b.roomSummary}
                      </Typography>
                    ) : null}
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {b.confirmationRef ? (
                        <>
                          Ref: <strong>{b.confirmationRef}</strong>
                          {b.totalPrice != null
                            ? ` · ${b.currency || 'GBP'} ${Number(b.totalPrice).toFixed(2)}`
                            : ''}
                        </>
                      ) : b.totalPrice != null ? (
                        <>
                          {b.currency || 'GBP'} {Number(b.totalPrice).toFixed(2)}
                        </>
                      ) : null}
                    </Typography>
                    <Divider sx={{ mt: 2 }} />
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </div>
      </main>

      <Dialog open={deleteOpen} onClose={closeDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Delete your account?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This permanently removes your account from our database. You will not be able to sign in
            with this email again unless you register a new account.
          </Typography>
          {deleteError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          ) : null}
          <TextField
            autoFocus
            margin="dense"
            label="Confirm with your password"
            type="password"
            fullWidth
            variant="outlined"
            value={deletePassword}
            onChange={(e) => {
              setDeletePassword(e.target.value);
              setDeleteError('');
            }}
            disabled={deleteLoading}
            autoComplete="current-password"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDeleteDialog} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={22} color="inherit" /> : 'Delete account'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Profile;
